import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";
import api, { extractApiError } from "@/lib/api";

const ROLES = ["client", "wholesale", "driver", "farmer", "admin"];

export default function AdminCreateUserDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    email: "",
    name: "",
    role: "client",
    state: "",
    grant_membership_months: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!form.email || !form.name) {
      toast.error("Email and name are required.");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        email: form.email.trim(),
        name: form.name.trim(),
        role: form.role,
        state: form.state.trim() || null,
        grant_membership_months: form.grant_membership_months
          ? parseInt(form.grant_membership_months, 10)
          : null,
      };
      await api.post("/admin/users/create", body);
      toast.success(`${body.name} added as ${body.role}.`);
      setOpen(false);
      setForm({ email: "", name: "", role: "client", state: "", grant_membership_months: "" });
      onCreated?.();
    } catch (err) {
      toast.error(extractApiError(err, "Could not create user."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="admin-add-user-open" className="bg-emerald-800 hover:bg-emerald-700 text-stone-50">
          <UserPlus size={16} className="mr-2" /> Add user manually
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-stone-50 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-stone-900">Add a user</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm font-sans">
          <div>
            <Label htmlFor="cu-email" className="text-xs font-semibold text-stone-800">Email</Label>
            <Input id="cu-email" data-testid="cu-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 bg-white" placeholder="name@example.com" />
          </div>
          <div>
            <Label htmlFor="cu-name" className="text-xs font-semibold text-stone-800">Full name</Label>
            <Input id="cu-name" data-testid="cu-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 bg-white" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold text-stone-800">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="cu-role" className="mt-2 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="cu-state" className="text-xs font-semibold text-stone-800">State</Label>
              <Input id="cu-state" data-testid="cu-state" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="mt-2 bg-white" placeholder="TX" />
            </div>
          </div>
          <div>
            <Label htmlFor="cu-months" className="text-xs font-semibold text-stone-800">
              Grant membership (months, optional)
            </Label>
            <Input
              id="cu-months"
              data-testid="cu-months"
              type="number"
              min="1"
              max="120"
              value={form.grant_membership_months}
              onChange={(e) => setForm({ ...form, grant_membership_months: e.target.value })}
              className="mt-2 bg-white"
              placeholder="12"
            />
            <p className="text-xs text-stone-500 mt-1">Leave blank to skip. 12 = one year.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="cu-submit" className="bg-emerald-800 hover:bg-emerald-700 text-stone-50">
            {submitting ? "Creating…" : "Create user"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
